import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../mutation-types';
import CampaignsAPI from '../../api/campaigns';
import AnalyticsHelper from '../../helper/AnalyticsHelper';
import { CAMPAIGNS_EVENTS } from '../../helper/AnalyticsHelper/events';
import { CAMPAIGN_TYPES } from 'shared/constants/campaign';
import { INBOX_TYPES } from 'dashboard/helper/inbox';

export const state = {
  records: [],
  campaignMetrics: {},
  campaignMessages: [],
  campaignPreviewTemplateParams: null,
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isFetchingMetrics: false,
  },
  meta: {
    currentPage: 1,
    totalCount: 0,
  },
};

export const getters = {
  getUIFlags(_state) {
    return _state.uiFlags;
  },
  getCampaigns:
    _state =>
    (campaignType, inboxChannelTypes = null, inboxId = null) => {
      let filteredRecords = _state.records.filter(
        record => record.campaign_type === campaignType
      );

      if (inboxChannelTypes && Array.isArray(inboxChannelTypes)) {
        filteredRecords = filteredRecords.filter(record => {
          return (
            record.inbox &&
            inboxChannelTypes.includes(record.inbox.channel_type)
          );
        });
      }

      if (inboxId) {
        filteredRecords = filteredRecords.filter(
          record => record.inbox?.id === Number(inboxId)
        );
      }

      return filteredRecords.sort((a1, a2) => a1.id - a2.id);
    },
  getSMSCampaigns: (_state, _getters) => inboxId => {
    const smsChannelTypes = [INBOX_TYPES.SMS, INBOX_TYPES.TWILIO];
    return _getters.getCampaigns(
      CAMPAIGN_TYPES.ONE_OFF,
      smsChannelTypes,
      inboxId
    );
  },
  getWhatsAppCampaigns: (_state, _getters) => inboxId => {
    const whatsappChannelTypes = [INBOX_TYPES.WHATSAPP];
    return _getters.getCampaigns(
      CAMPAIGN_TYPES.ONE_OFF,
      whatsappChannelTypes,
      inboxId
    );
  },
  getLiveChatCampaigns: (_state, _getters) => inboxId => {
    const liveChatChannelTypes = [INBOX_TYPES.WEB];
    return _getters.getCampaigns(
      CAMPAIGN_TYPES.ONGOING,
      liveChatChannelTypes,
      inboxId
    );
  },
  getAllCampaigns: _state => {
    return _state.records;
  },
  getMeta: _state => _state.meta,
  getCampaignMetrics: _state => _state.campaignMetrics,
  getCampaignMessages: _state => _state.campaignMessages,
  getCampaignPreviewTemplateParams: _state =>
    _state.campaignPreviewTemplateParams,
};

export const actions = {
  get: async function getCampaigns({ commit }, params = {}) {
    commit(types.SET_CAMPAIGN_UI_FLAG, {
      isFetching: true,
      hasFetchError: false,
    });
    try {
      const response = await CampaignsAPI.get(params);
      const payload = response.data.payload || response.data;
      const meta = response.data.meta || {};
      commit(types.SET_CAMPAIGNS, payload);
      commit('SET_META', meta);
    } catch (error) {
      commit(types.SET_CAMPAIGN_UI_FLAG, { hasFetchError: true });
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isFetching: false });
    }
  },
  create: async function createCampaign({ commit }, campaignObj) {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isCreating: true });
    try {
      const response = await CampaignsAPI.create(campaignObj);
      commit(types.ADD_CAMPAIGN, response.data);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isCreating: false });
    }
  },
  update: async ({ commit }, { id, ...updateObj }) => {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isUpdating: true });
    try {
      const response = await CampaignsAPI.update(id, updateObj);
      AnalyticsHelper.track(CAMPAIGNS_EVENTS.UPDATE_CAMPAIGN);
      commit(types.EDIT_CAMPAIGN, response.data);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isUpdating: false });
    }
  },
  delete: async ({ commit }, id) => {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isDeleting: true });
    try {
      await CampaignsAPI.delete(id);
      AnalyticsHelper.track(CAMPAIGNS_EVENTS.DELETE_CAMPAIGN);
      commit(types.DELETE_CAMPAIGN, id);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isDeleting: false });
    }
  },
  fetchMetrics: async ({ commit }, campaignId) => {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isFetchingMetrics: true });
    try {
      const response = await CampaignsAPI.metrics(campaignId);
      commit(types.SET_CAMPAIGN_METRICS, response.data);
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isFetchingMetrics: false });
    }
  },
  createAbTest: async ({ dispatch }, params) => {
    const response = await CampaignsAPI.createAbTest(params);
    await dispatch('get');
    return response.data;
  },
  launch: async ({ commit }, { id, ...params }) => {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isUpdating: true });
    try {
      const response = await CampaignsAPI.launch(id, params);
      commit(types.EDIT_CAMPAIGN, response.data);
      return response.data;
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isUpdating: false });
    }
  },
  cancel: async ({ commit }, id) => {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isUpdating: true });
    try {
      const response = await CampaignsAPI.cancel(id);
      commit(types.EDIT_CAMPAIGN, response.data);
      return response.data;
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isUpdating: false });
    }
  },
};

export const mutations = {
  [types.SET_CAMPAIGN_UI_FLAG](_state, data) {
    _state.uiFlags = {
      ..._state.uiFlags,
      ...data,
    };
  },

  [types.ADD_CAMPAIGN]: MutationHelpers.create,
  [types.SET_CAMPAIGNS]: MutationHelpers.set,
  [types.EDIT_CAMPAIGN]: MutationHelpers.update,
  [types.DELETE_CAMPAIGN]: MutationHelpers.destroy,
  [types.SET_CAMPAIGN_METRICS](_state, data) {
    _state.campaignMetrics = data.metrics || {};
    _state.campaignMessages = data.campaign_messages || [];
    _state.campaignPreviewTemplateParams = data.preview_template_params || null;
  },
  SET_META(_state, data) {
    _state.meta = {
      currentPage: data.current_page || 1,
      totalCount: data.count || 0,
    };
  },
};

export default {
  namespaced: true,
  actions,
  state,
  getters,
  mutations,
};
